/**
 * PanelOpinie — owner panel dla Verified Reviews.
 *
 * Sekcje:
 *  1. Stats hero (total, published, average rating, pending requests)
 *  2. Lista opinii z tabsami (Wszystkie / Do moderacji / Opublikowane / Wysłane)
 *  3. Każda opinia: gwiazdki, autor, treść, źródło (manual/auto_30d), akcje (publikuj/ukryj/odpowiedz)
 *
 * Backend: GET /api/reviews/my + POST /api/reviews/{id}/moderate + /reply
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Star, MessageSquare, ShieldCheck, Send, Eye, EyeOff,
  Loader2, ChevronRight, Mail, Clock, CheckCircle2, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import StudioShell from '@/components/studio/StudioShell';
import { API } from '@/utils/api';

const STATUS_LABEL = {
  pending: { text: 'Wysłana prośba', color: 'amber', bg: 'bg-amber-500/15', border: 'border-amber-500/30', dot: 'bg-amber-400' },
  submitted: { text: 'Do moderacji', color: 'violet', bg: 'bg-violet-500/15', border: 'border-violet-500/30', dot: 'bg-violet-400' },
  published: { text: 'Opublikowana', color: 'emerald', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', dot: 'bg-emerald-400' },
  hidden: { text: 'Ukryta', color: 'zinc', bg: 'bg-zinc-500/15', border: 'border-zinc-500/30', dot: 'bg-zinc-400' },
};

function Stars({ rating, size = 'sm' }) {
  const px = size === 'lg' ? 'w-5 h-5' : 'w-3.5 h-3.5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${px} ${n <= (rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-zinc-700'}`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function relativeDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'przed chwilą';
    if (diff < 3600) return `${Math.floor(diff / 60)} min temu`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} godz. temu`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)} dni temu`;
    return d.toLocaleDateString('pl-PL');
  } catch {
    return '';
  }
}

function ReviewCard({ review, onAction, onReply }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState(review.owner_reply || '');
  const status = STATUS_LABEL[review.status] || STATUS_LABEL.pending;

  return (
    <article className={`rounded-2xl bg-white/[0.02] border ${status.border} p-4 sm:p-5`} data-testid={`review-card-${review.id}`}>
      <header className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="text-white font-semibold text-sm">{review.author_display || review.author_name}</p>
            {review.verified && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-bold border border-emerald-500/30">
                <ShieldCheck className="w-2.5 h-2.5" /> ZWERYFIKOWANA
              </span>
            )}
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${status.bg} text-${status.color}-300 text-[10px] font-bold border ${status.border}`}>
              <span className={`w-1 h-1 rounded-full ${status.dot}`} />
              {status.text}
            </span>
          </div>
          {review.rating ? <Stars rating={review.rating} /> : <span className="text-zinc-600 text-xs italic">jeszcze nie oceniono</span>}
        </div>
        <div className="flex flex-col items-end gap-0.5 text-right flex-shrink-0">
          <span className="text-zinc-500 text-[10px]">{relativeDate(review.submitted_at || review.created_at)}</span>
          {review.source === 'auto_30d' && (
            <span className="text-amber-300 text-[9px] uppercase tracking-wider font-semibold">auto</span>
          )}
        </div>
      </header>

      {review.title && (
        <p className="text-white font-semibold text-sm mb-1.5">„{review.title}"</p>
      )}
      {review.text ? (
        <p className="text-zinc-300 text-sm leading-relaxed mb-3">{review.text}</p>
      ) : (
        <p className="text-zinc-500 text-xs italic mb-3 flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          Klient jeszcze nie wystawił opinii — link wysłany do <span className="text-zinc-400">{review.author_email}</span>
        </p>
      )}

      {/* Owner reply (if exists) */}
      {review.owner_reply && (
        <div className="bg-violet-500/8 border border-violet-500/20 rounded-xl p-3 mb-3">
          <p className="text-violet-300 text-[10px] uppercase tracking-wider font-bold mb-1">Twoja odpowiedź · {relativeDate(review.owner_reply_at)}</p>
          <p className="text-zinc-200 text-sm">{review.owner_reply}</p>
        </div>
      )}

      {/* Reply composer */}
      {replyOpen && (
        <div className="mb-3">
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Twoja odpowiedź będzie widoczna publicznie obok opinii…"
            rows={3}
            maxLength={1000}
            data-testid={`reply-input-${review.id}`}
            className="bg-white/[0.03] border-white/10 text-white"
          />
          <div className="flex gap-2 mt-2">
            <Button
              type="button"
              size="sm"
              onClick={() => { onReply(review.id, replyText); setReplyOpen(false); }}
              disabled={!replyText.trim() || replyText.trim().length < 2}
              data-testid={`reply-submit-${review.id}`}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Send className="w-3.5 h-3.5 mr-1" /> Zapisz odpowiedź
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setReplyOpen(false)} className="text-zinc-400">
              Anuluj
            </Button>
          </div>
        </div>
      )}

      {/* Actions */}
      {review.status !== 'hidden' && review.text && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
          {review.status === 'submitted' && (
            <Button
              type="button"
              size="sm"
              onClick={() => onAction(review.id, 'publish')}
              data-testid={`publish-btn-${review.id}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Eye className="w-3.5 h-3.5 mr-1" /> Opublikuj
            </Button>
          )}
          {review.status === 'published' && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onAction(review.id, 'hide')}
              data-testid={`hide-btn-${review.id}`}
              className="border-white/10 text-zinc-300 hover:bg-white/5"
            >
              <EyeOff className="w-3.5 h-3.5 mr-1" /> Ukryj
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setReplyOpen(!replyOpen)}
            data-testid={`reply-toggle-${review.id}`}
            className="text-violet-300 hover:bg-violet-500/10"
          >
            <MessageSquare className="w-3.5 h-3.5 mr-1" /> {review.owner_reply ? 'Edytuj odpowiedź' : 'Odpowiedz'}
          </Button>
        </div>
      )}
    </article>
  );
}

function StatTile({ icon: Icon, label, value, suffix, color }) {
  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/5 p-4 sm:p-5">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}22`, border: `1px solid ${color}40` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-zinc-400 text-xs font-medium">{label}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl sm:text-3xl font-bold text-white tabular-nums">{value}</span>
        {suffix && <span className="text-zinc-500 text-sm">{suffix}</span>}
      </div>
    </div>
  );
}

export default function PanelOpinie() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, submitted: 0, published: 0, average_rating: 0 });
  const [tab, setTab] = useState('all'); // all | submitted | published | pending

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = () => ({ Authorization: `Bearer ${token}` });

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews/my`, { headers: headers() });
      setReviews(res.data?.items || []);
      setStats(res.data?.stats || { total: 0, pending: 0, submitted: 0, published: 0, average_rating: 0 });
    } catch {
      toast.error('Nie udało się załadować opinii');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (tab === 'all') return reviews;
    return reviews.filter((r) => r.status === tab);
  }, [tab, reviews]);

  const handleAction = async (id, action) => {
    try {
      await axios.post(`${API}/reviews/${id}/moderate`, { action }, { headers: headers() });
      toast.success(action === 'publish' ? 'Opinia opublikowana!' : 'Opinia ukryta');
      fetchReviews();
    } catch {
      toast.error('Nie udało się wykonać akcji');
    }
  };

  const handleReply = async (id, text) => {
    if (!text || text.trim().length < 2) return;
    try {
      await axios.post(`${API}/reviews/${id}/reply`, { text }, { headers: headers() });
      toast.success('Odpowiedź zapisana');
      fetchReviews();
    } catch {
      toast.error('Nie udało się zapisać odpowiedzi');
    }
  };

  if (loading) {
    return <StudioShell mode="workspace" activeKey="opinie" contextLabel="Opinie"><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 text-violet-600 animate-spin" /></div></StudioShell>;
  }

  return (
    <StudioShell mode="workspace" activeKey="opinie" contextLabel="Opinie">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6" data-testid="panel-opinie">
        {/* Header */}
        <header>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-300 text-[10px] uppercase tracking-[0.2em] font-bold">Trust layer · zweryfikowane</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Opinie klientów</h1>
          <p className="text-zinc-400 text-sm mt-1">
            Opinie pochodzą tylko od osób, które napisały do Ciebie przez APISAI — to prawdziwy trust signal dla nowych klientów.
          </p>
        </header>

        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="opinie-stats">
          <StatTile icon={ShieldCheck} label="Opublikowane" value={stats.published} color="#10B981" />
          <StatTile icon={Star} label="Średnia ocen" value={stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '—'} suffix="/ 5" color="#F59E0B" />
          <StatTile icon={Sparkles} label="Do moderacji" value={stats.submitted} color="#F97316" />
          <StatTile icon={Mail} label="Wysłane prośby" value={stats.pending} color="#3B82F6" />
        </section>

        {/* Tabs */}
        <section>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="opinie-tabs">
            {[
              { key: 'all', label: 'Wszystkie', count: stats.total },
              { key: 'submitted', label: 'Do moderacji', count: stats.submitted },
              { key: 'published', label: 'Opublikowane', count: stats.published },
              { key: 'pending', label: 'Wysłane prośby', count: stats.pending },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                  tab === t.key
                    ? 'bg-violet-600 text-white shadow-md shadow-violet-500/20'
                    : 'bg-white/[0.03] text-zinc-400 hover:bg-white/[0.06] border border-white/5'
                }`}
              >
                {t.label}
                {Number(t.count) > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] tabular-nums ${tab === t.key ? 'bg-white/20' : 'bg-zinc-700/50'}`}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-white/10 p-10 text-center" data-testid="opinie-empty">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-violet-500/10 flex items-center justify-center mb-3">
                <MessageSquare className="w-7 h-7 text-violet-300" />
              </div>
              <p className="text-white font-semibold">
                {tab === 'all'
                  ? 'Jeszcze nie masz opinii'
                  : tab === 'pending' ? 'Brak wysłanych próśb'
                  : tab === 'submitted' ? 'Brak nowych opinii do moderacji'
                  : 'Brak opublikowanych opinii'}
              </p>
              <p className="text-zinc-500 text-xs mt-1 max-w-md mx-auto">
                {tab === 'all'
                  ? 'Zweryfikowane opinie wysyłamy automatycznie 30 dni po pierwszym kontakcie klienta — albo możesz poprosić ręcznie z widoku „Wiadomości".'
                  : 'Tu pojawią się odpowiednie wpisy, gdy będą.'}
              </p>
              <Button onClick={() => navigate('/panel/wiadomosci')} className="mt-4 bg-violet-600 hover:bg-violet-700">
                <ChevronRight className="w-4 h-4 mr-1" /> Przejdź do wiadomości
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((r) => (
                <ReviewCard key={r.id} review={r} onAction={handleAction} onReply={handleReply} />
              ))}
            </div>
          )}
        </section>

        {/* Footer info */}
        <section className="rounded-2xl bg-gradient-to-r from-emerald-500/10 to-violet-500/10 border border-emerald-400/20 p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="flex-1">
            <p className="text-white font-bold text-sm">Jak działają zweryfikowane opinie?</p>
            <p className="text-zinc-300 text-xs leading-relaxed mt-1">
              Każda opinia powstaje przez unikalny link email — tylko klient, który skontaktował się przez APISAI, może ją wystawić.
              Oceny ≥3 publikujemy automatycznie. Niższe trafiają do moderacji — możesz odpowiedzieć lub ukryć.
            </p>
          </div>
        </section>
      </div>
    </StudioShell>
  );
}
